'use client';

import { useEffect, useRef } from 'react';
import { useConvexAuth } from 'convex/react';

import { api, useMutation, useQuery } from '@/lib/convex';
import {
  clearPendingSignupLocale,
  getPendingAuthLocaleCookie,
} from '@/lib/auth-locale-preference';
import { shouldSyncPendingLocale } from '@/i18n/locale-preference';
import { useSyncBrowserTimezone } from '@/components/auth/use-sync-browser-timezone';
import { timezoneForLanguageSave } from '@/lib/regional-timezone';

export function AuthLocalePreferenceSync() {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { data: profile } = useQuery(
    api.users.queries.getProfile,
    isAuthenticated ? {} : 'skip'
  );
  const updateRegionalSettings = useMutation(
    api.users.mutations.updateRegionalSettings
  );
  const syncInFlightRef = useRef(false);
  const { pending: timezoneSyncPending } = useSyncBrowserTimezone(
    isLoading || !isAuthenticated || !profile ? null : profile.settings
  );

  // Runs after the browser zone sync so its write sees the synced zone; it
  // re-sends the saved zone unchanged, which keeps an unchosen zone's source.
  useEffect(() => {
    if (
      isLoading ||
      !isAuthenticated ||
      !profile ||
      syncInFlightRef.current ||
      timezoneSyncPending
    ) {
      return;
    }

    const pendingLocale = getPendingAuthLocaleCookie();
    if (!pendingLocale) {
      return;
    }

    if (!shouldSyncPendingLocale(pendingLocale, profile.settings.language)) {
      clearPendingSignupLocale(pendingLocale);
      return;
    }

    syncInFlightRef.current = true;
    void updateRegionalSettings({
      language: pendingLocale,
      timezone: timezoneForLanguageSave({
        persistedTimezone: profile.settings.timezone,
      }),
    })
      .then(() => {
        clearPendingSignupLocale(pendingLocale);
      })
      .catch((error: unknown) => {
        syncInFlightRef.current = false;
        console.error('[auth-locale-sync] failed to update locale', error);
      });
  }, [
    isAuthenticated,
    isLoading,
    profile,
    timezoneSyncPending,
    updateRegionalSettings,
  ]);

  return null;
}
